import useAuthContext from '@salesforce/commerce-sdk-react/hooks/useAuthContext';
import { useQuery } from '@tanstack/react-query';
import type { UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { useCustomPreferenceOrDefault } from '../../utils/api';
import { CustomPreference } from '../../utils/api/CustomPreference';
import { isServer } from '../../utils/utils';
import { bffGet } from '../bff';
import { BffQueryKey } from '../model/BffQueryKey';
import type { BFFHttpError } from '../model/BFFHttpError';
import type { CustomerContext } from './model/CustomerContext';

export const useCustomerContextGet = (): UseQueryResult<CustomerContext> => {
    const pwaSurlatablePreview = useCustomPreferenceOrDefault(
        CustomPreference.PWA_SURLATABLE_PREVIEW,
        false,
    );
    const authContext = useAuthContext();

    // eslint-disable-next-line @tanstack/query/prefer-query-object-syntax
    return useQuery<CustomerContext, BFFHttpError>({
        queryFn: async (): Promise<CustomerContext> => {
            if (!isServer) await window.sfccSessionCreationPromise;
            const authData = await authContext.ready();
            if (!authData.usid) {
                return {} as CustomerContext;
            }
            return bffGet<CustomerContext>(`/contexts/${authData.usid}`, {
                httpParams: [],
                accessToken: authData.access_token,
                customerId: authData.customer_id,
            }).catch(() => ({}) as CustomerContext);
        },
        queryKey: [BffQueryKey.CUSTOMER_CONTEXT],
        enabled: !isServer && !!pwaSurlatablePreview,
        refetchOnWindowFocus: false,
    } as UseQueryOptions<CustomerContext, BFFHttpError>);
};
